import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Typography, Button, Card, CardActions } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { DashboardHelpers } from 'utils';
import { TimePeriods } from 'utils/ReadingsHelpers';
import _ from 'lodash';
import EventDetailsSwitch from './EventDetailsSwitch';
import { useEvents, useOrganizations, useProperties, useDevices, useFields, useTelemetry, useSettings } from 'hooks';

const useStyles = makeStyles((theme) => ({
  root: { overflow: 'visible' },
  alert: {
    marginBottom: theme.spacing(3),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
    width: '100%',
    flexGrow: 1,
  },
  formGroupGrid: {
    marginBottom: theme.spacing(3),
    flexGrow: 1,
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  fieldHint: {
    margin: theme.spacing(1, 0),
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  tags: {
    marginTop: theme.spacing(1),
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  modal: {
    position: 'fixed',
    top: '5px',
    right: '5px',
    width: '50%',
    backgroundColor: 'rgb(255,255,255, 1)',
    boxShadow: 24,
  },
  modalActionCancel: {
    marginLeft: '2%',
  },
  modalActionPrimary: {
    marginLeft: '5%',
  },
  srcClickLink: { textDecoration: 'underline', color: 'blue', cursor: 'pointer' },
}));

const EventDetails = ({ setSelectedId, selectedEvent, hide }) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const { showEventDetails } = useEvents();
  const { organizations } = useOrganizations();
  const { properties, setSelectedProperty } = useProperties();
  const { devices, setSelectedDevice } = useDevices();
  const { fields, setSelectedField } = useFields();
  const { setTelemetryPeriod } = useTelemetry();
  const { setDashView } = useSettings();
  let title = '';
  let timeString = '';
  let currentOrganization = {};

  let created = new Date(selectedEvent.created_at).toLocaleDateString('en-US');
  let deleted = new Date(selectedEvent.deleted_at).toLocaleDateString('en-US');
  let updated = new Date(selectedEvent.updated_at).toLocaleDateString('en-US');

  const handleSourceClick = () => {
    let parents = DashboardHelpers.getParentsOfLayer(
      selectedEvent.layer,
      selectedEvent.layer_id,
      devices,
      fields,
      properties
    );

    if ((!parents || !parents.field || !parents.property) && selectedEvent.layer === 'device') {
      history.push('/devices');
    } else if (selectedEvent.layer === 'device' && parents.field && parents.property) {
      setSelectedProperty(parents.property.id);
      setSelectedField(parents.field.id);
      setSelectedDevice(_.find(devices, { id: selectedEvent.layer_id }));
      setDashView('charts');
      setTelemetryPeriod(TimePeriods.OneWeek);
    } else if (selectedEvent.layer === 'field' && parents.property) {
      setSelectedProperty(parents.property.id);
      setSelectedField(selectedEvent.layer_id);
      setSelectedDevice({});
      setTelemetryPeriod(TimePeriods.OneDay);
      setDashView('stats');
    } else if (selectedEvent.layer === 'property') {
      setSelectedProperty(selectedEvent.layer_id);
      setSelectedField('');
      setSelectedDevice({});
      setDashView('map');
    }
    hide();
    history.push('/');
  };

  if (selectedEvent.data) {
    const obj = DashboardHelpers.getObjectById(selectedEvent.layer_id, organizations, properties, fields, devices);
    if (obj && obj.name) {
      title = obj.name;
    } else if (obj?.hw_address) {
      title = DashboardHelpers.trimHWAddress(obj.hw_address);
    }
    currentOrganization = _.find(organizations, (organization) => {
      return organization.id === selectedEvent.organization_id;
    });

    let eventTime = new Date(selectedEvent.time);
    timeString = eventTime.toLocaleTimeString('en-US');
  } else {
    setSelectedId('');
  }

  return (
    <Card className={classes.modal}>
      <Grid container padding={2}>
        <Grid item container xs={6}>
          <Grid item xs={12} padding={2}>
            <Typography variant="h4">{t('event_info')}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              onClick={() => {
                handleSourceClick();
              }}
            >
              {t('layer_id')}: <span className={title ? classes.srcClickLink : null}>{title ? title : t('none')}</span>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              {t('layer')}: {selectedEvent.layer}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              {t('created_at')}: {created}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              {t('updated_at')}: {updated}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              {t('deleted_at')}: {deleted}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              {t('time')}: {timeString}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              {t('organization_id')}: {currentOrganization?.name ? currentOrganization.name : t('none')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              {t('type')}: {selectedEvent.type}
            </Typography>
          </Grid>
        </Grid>
        <Grid item container xs={6}>
          <Grid item xs={12} padding={2}>
            <Typography variant="h5">{!showEventDetails ? t('event_data') : t('event_data_advanced')}</Typography>
          </Grid>
          <Grid item xs={12}>
            {showEventDetails && (
              <Typography>
                {t('id')}: {selectedEvent.id}
              </Typography>
            )}

            {!!showEventDetails && selectedEvent.data
              ? Object.keys(selectedEvent.data).map((keyName, i) => (
                  <Typography key={i}>
                    {keyName} : {selectedEvent.data[keyName]}
                  </Typography>
                ))
              : selectedEvent.data && (
                  <div>
                    <Typography>{selectedEvent.data['insight']}</Typography>
                    <Typography style={{ marginTop: 10 }} variant="subtitle2">
                      Switch to Advanced for Detailed Event Data
                    </Typography>
                  </div>
                )}
          </Grid>
        </Grid>
      </Grid>
      <CardActions>
        <Button onClick={hide} color="primary">
          {t('close')}
        </Button>
        <EventDetailsSwitch />
      </CardActions>
    </Card>
  );
};

EventDetails.propTypes = {
  selectedEvent: PropTypes.object,
  hide: PropTypes.func,
  setSelectedId: PropTypes.func,
  properties: PropTypes.array,
  setSelectedField: PropTypes.func,
  setSelectedDevice: PropTypes.func,
  setSelectedProperty: PropTypes.func,
  setDashView: PropTypes.func,
};

EventDetails.defaultProps = {
  selectedEvent: {},
  hide: () => {},
  setSelectedId: () => {},
  setSelectedField: () => {},
  setSelectedDevice: () => {},
  setSelectedProperty: () => {},
  setDashView: () => {},
};

export default EventDetails;
